import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { incompleteProfilesReportAPI } from '../../services/api';
import { Download, ArrowLeft, FileSpreadsheet, Loader2, CheckCircle2, AlertTriangle, Search } from 'lucide-react';
import CompletionRing from '../../components/investors/CompletionRing';

const TYPES = ['Individual', 'Company', 'LLP', 'Other'];
const THRESHOLDS = [
  { v: 50, label: '< 50% (critical)' },
  { v: 85, label: '< 85% (needs attention)' },
  { v: 99, label: 'All incomplete (< 100%)' },
];

const IncompleteProfilesReport = () => {
  const navigate = useNavigate();
  const [maxPercent, setMaxPercent] = useState(99);
  const [typeFilter, setTypeFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(new Set());

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await incompleteProfilesReportAPI.get({
        maxPercent,
        typeFilter: typeFilter || undefined,
      });
      setRows(res.data?.rows || []);
      setRowCount(res.data?.row_count || 0);
    } catch (e) {
      console.error(e);
      setRows([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRows(); }, [maxPercent, typeFilter]);  // eslint-disable-line

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await incompleteProfilesReportAPI.export({
        maxPercent,
        typeFilter: typeFilter || undefined,
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'incomplete_investor_profiles.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export');
    } finally {
      setExporting(false);
    }
  };

  const toggleExpand = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filtered = rows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (r.account_name || '').toLowerCase().includes(q) ||
      (r.pan || '').toLowerCase().includes(q) ||
      (r.contact_name || '').toLowerCase().includes(q) ||
      (r.investor_lead_name || '').toLowerCase().includes(q) ||
      (r.missing_summary || '').toLowerCase().includes(q)
    );
  });

  // KPIs
  const kpiCritical = rows.filter((r) => r.percent < 50).length;
  const kpiAmber = rows.filter((r) => r.percent >= 50 && r.percent < 85).length;
  const kpiNearComplete = rows.filter((r) => r.percent >= 85).length;
  const avgPercent = rows.length
    ? Math.round(rows.reduce((s, r) => s + (r.percent || 0), 0) / rows.length)
    : 0;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="incomplete-profiles-report-page">
      <Link to="/investors/reports" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4">
        <ArrowLeft size={16} className="mr-1" /> Back to Reports
      </Link>

      <div className="flex justify-between items-start mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Incomplete Investor Profiles</h1>
          <p className="text-sm text-gray-500 mt-1">
            Investor Accounts with profile completion below the selected threshold. Click any row to reveal the full list of missing applicable fields, or open the account to fix them in place.
          </p>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting || filtered.length === 0}
          className="inline-flex items-center px-3 py-2 text-sm rounded-lg border border-green-300 text-green-700 hover:bg-green-50 disabled:opacity-50 shrink-0"
          data-testid="export-incomplete-btn"
        >
          {exporting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <FileSpreadsheet size={14} className="mr-1.5" />}
          Export to Excel
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={maxPercent}
          onChange={(e) => setMaxPercent(Number(e.target.value))}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
          data-testid="threshold-filter"
        >
          {THRESHOLDS.map((t) => (
            <option key={t.v} value={t.v}>{t.label}</option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm bg-white"
          data-testid="type-filter"
        >
          <option value="">All Investor Types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by account, PAN, contact, lead, or missing field..."
            data-testid="incomplete-search"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
          />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Accounts Below Threshold</p>
          <p className="text-2xl font-bold text-gray-900 mt-1" data-testid="kpi-total">{rowCount}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Critical (&lt; 50%)</p>
          <p className="text-2xl font-bold text-red-600 mt-1" data-testid="kpi-critical">{kpiCritical}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Needs Attention (50–85%)</p>
          <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="kpi-amber">{kpiAmber}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wider">Near Complete (≥ 85%)</p>
          <p className="text-2xl font-bold text-emerald-600 mt-1" data-testid="kpi-near-complete">{kpiNearComplete}</p>
        </div>
      </div>

      {loading ? (
        <div className="p-12 text-center text-gray-500">Loading report...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border rounded-lg p-12 text-center">
          <CheckCircle2 size={36} className="mx-auto text-emerald-300 mb-3" />
          <p className="text-gray-500">
            {rowCount === 0
              ? 'No incomplete profiles at this threshold. Either all accounts are fully populated, or no accounts exist for the selected type.'
              : 'No rows match your search.'}
          </p>
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <div className="px-4 py-2.5 bg-gray-50 border-b text-xs text-gray-500 flex items-center gap-2">
            <AlertTriangle size={13} className="text-amber-500" />
            Showing {filtered.length} account{filtered.length !== 1 ? 's' : ''} — average completion {avgPercent}%. Click a row to see the full missing-field list.
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="incomplete-profiles-table">
              <thead>
                <tr className="bg-white border-b text-left text-gray-600 font-medium">
                  <th className="px-4 py-3 w-12">% Complete</th>
                  <th className="px-4 py-3">Account</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">PAN</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Investor Lead</th>
                  <th className="px-4 py-3 text-center">Missing</th>
                  <th className="px-4 py-3">Top Gaps</th>
                  <th className="px-4 py-3 w-20" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const isOpen = expanded.has(r.account_id);
                  const topGaps = r.missing_fields.slice(0, 3).join(', ');
                  const moreCount = Math.max(0, r.missing_fields.length - 3);
                  return (
                    <React.Fragment key={r.account_id}>
                      <tr
                        className="border-b hover:bg-blue-50/40 cursor-pointer transition-colors"
                        onClick={() => toggleExpand(r.account_id)}
                        data-testid={`incomplete-row-${r.account_id}`}
                      >
                        <td className="px-4 py-3">
                          <CompletionRing
                            percent={r.percent}
                            missing={r.missing_fields}
                            size={36}
                          />
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{r.account_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{r.type_of_investor || '—'}</td>
                        <td className="px-4 py-3 text-gray-700 font-mono text-xs">{r.pan || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{r.contact_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{r.investor_lead_name || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center min-w-[2.25rem] px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-semibold">
                            {r.missing_count}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">
                          {topGaps}{moreCount > 0 && <span className="text-gray-400"> + {moreCount} more</span>}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/investors/accounts/${r.account_id}`); }}
                            className="text-xs px-2 py-1 rounded-md border border-blue-200 text-blue-700 hover:bg-blue-50"
                            data-testid={`open-account-${r.account_id}`}
                          >
                            Open
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-amber-50/40 border-b">
                          <td />
                          <td colSpan={8} className="px-4 py-3">
                            <div className="flex items-start gap-2 text-xs text-gray-700">
                              <AlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" />
                              <div>
                                <span className="font-medium text-gray-800">All {r.missing_count} missing applicable fields:</span>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {r.missing_fields.map((m, i) => (
                                    <span
                                      key={i}
                                      className="inline-block px-2 py-0.5 rounded-md bg-white border border-amber-200 text-amber-800"
                                      data-testid={`missing-pill-${r.account_id}-${i}`}
                                    >
                                      {m}
                                    </span>
                                  ))}
                                </div>
                                <div className="mt-2 text-gray-500">
                                  Filled <strong>{r.filled}</strong> of <strong>{r.total}</strong> applicable fields for this {r.type_of_investor || 'investor'}.
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncompleteProfilesReport;
